import React, { useEffect } from "react";
import { X } from "lucide-react";
import YmdDateInput from "../ui/YmdDateInput";
import { normalizeBillingAccount } from "../../utils/helpers";
import { formatGlCodeLabel, glCodesForDropdown } from "../../utils/glCodes";
import { getCurrentInvoiceMonthEndDate } from "../../utils/invoiceMonthDisplay";

import CustomSelect from "../ui/CustomSelect";
const fieldClass =
  "w-full border border-stone-200 rounded-md px-2.5 py-2 text-sm text-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-400 focus:border-stone-400 disabled:bg-stone-50";

const numberFieldClass = `${fieldClass} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`;

function parsePreviewAmount(value) {
  if (value === "" || value == null) return null;
  const n = parseFloat(String(value).trim());
  return Number.isFinite(n) ? n : null;
}

function formatPreviewAmount(value) {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Same arithmetic check as the review table: MRC + taxes vs total. */
function getMrcTaxTotalCheck(row) {
  if (!row) return null;
  const mrc = parsePreviewAmount(row.monthly_mrc);
  const total = parsePreviewAmount(row.total_invoice_amount);
  if (mrc === null || total === null || total === 0) return null;
  const taxesRaw = String(row.taxes_and_surcharges ?? "").trim();
  const taxes = taxesRaw ? parsePreviewAmount(row.taxes_and_surcharges) ?? 0 : 0;
  const sum = mrc + taxes;
  const diff = Math.abs(total - sum);
  if (diff < 0.01) return null;
  return { sum, total, diff };
}

/**
 * Side-by-side PDF preview + extracted fields for Auto-extract review.
 * Field edits call onUpdate so the review table stays in sync.
 */
const InvoiceExtractionPdfPreviewModal = ({
  row,
  pdfUrl,
  loading,
  error,
  busy,
  providerOptions = [],
  glCodes = [],
  accountsForRow,
  onUpdate,
  onClose,
  onSaveAndClose,
  onSaveAndNext,
  hasNextFlagged = false,
}) => {
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        if (typeof onSaveAndClose === "function") onSaveAndClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose, onSaveAndClose]);

  if (!row) return null;

  const showFields = Object.prototype.hasOwnProperty.call(row, "invoice_number");

  const titleParts = [
    row.provider || row.originalFilename || "Invoice",
    row.invoice_number ? `Invoice ${row.invoice_number}` : null,
    row.billing_account ? `Account ${row.billing_account}` : null,
  ].filter(Boolean);
  const accounts = typeof accountsForRow === "function" ? accountsForRow(row.provider) : [];
  const accountInList = accounts.some(
    (account) =>
      normalizeBillingAccount(account.billing_account) ===
      normalizeBillingAccount(row.billing_account),
  );
  const amountCheck = showFields ? getMrcTaxTotalCheck(row) : null;
  const totalFieldClass = amountCheck
    ? `${numberFieldClass} border-sky-300 bg-sky-50 focus:ring-sky-400 focus:border-sky-400`
    : numberFieldClass;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-5 bg-[#1c1c1a]/25 backdrop-blur-[4px]"
      style={{ WebkitBackdropFilter: "blur(4px)" }}
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-pdf-preview-title"
        className="flex h-[92vh] w-full max-w-[1400px] flex-col overflow-hidden rounded-xl border border-stone-200 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-stone-100 px-4 py-3">
          <h2
            id="invoice-pdf-preview-title"
            className="min-w-0 truncate text-base font-semibold text-stone-900"
          >
            {titleParts.join(" · ")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
            aria-label="Close preview"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className={`grid min-h-0 flex-1 ${showFields ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"}`}>
          <div className={`flex min-h-0 flex-col border-b border-stone-200 bg-stone-100 ${showFields ? "lg:border-b-0 lg:border-r" : ""}`}>
            <div className="flex items-center justify-between gap-3 bg-stone-800 px-3 py-2 text-xs text-stone-100">
              <span className="min-w-0 truncate" title={row.originalFilename}>
                {row.originalFilename || "invoice.pdf"}
              </span>
              {pdfUrl ? (
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 underline decoration-stone-400 underline-offset-2 hover:text-white"
                >
                  Open in new tab
                </a>
              ) : null}
            </div>
            <div className="min-h-0 flex-1 bg-stone-200">
              {loading ? (
                <div className="flex h-full items-center justify-center text-sm text-stone-600">
                  Loading PDF…
                </div>
              ) : error ? (
                <div className="flex h-full items-center justify-center px-6 text-center text-sm text-red-700">
                  {error}
                </div>
              ) : pdfUrl ? (
                <iframe
                  title={`Preview of ${row.originalFilename || "invoice PDF"}`}
                  src={pdfUrl}
                  className="h-full w-full border-0 bg-white"
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-stone-600">
                  PDF is not available.
                </div>
              )}
            </div>
          </div>

          {showFields ? (
          <div className="min-h-0 overflow-y-auto px-4 py-4">
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone-500">
                  Provider<span className="text-red-400">*</span>
                </label>
                <CustomSelect
                  className={fieldClass}
                  value={providerOptions.includes(row.provider) ? row.provider : ""}
                  onChange={(e) => onUpdate(row.fileId, "provider", e.target.value)}
                  disabled={busy}
                >
                  <option value="">Select provider</option>
                  {providerOptions.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </CustomSelect>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone-500">
                  Invoice Number<span className="text-red-400">*</span>
                </label>
                <input
                  className={fieldClass}
                  value={row.invoice_number}
                  onChange={(e) =>
                    onUpdate(row.fileId, "invoice_number", e.target.value)
                  }
                  disabled={busy}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone-500">
                  Invoice Date<span className="text-red-400">*</span>
                </label>
                <YmdDateInput
                  className={fieldClass}
                  value={row.invoice_date}
                  onChange={(e) =>
                    onUpdate(row.fileId, "invoice_date", e.target.value)
                  }
                  max={getCurrentInvoiceMonthEndDate()}
                  disabled={busy}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone-500">
                  Billing Account<span className="text-red-400">*</span>
                </label>
                <CustomSelect
                  className={fieldClass}
                  value={
                    accountInList
                      ? normalizeBillingAccount(row.billing_account)
                      : ""
                  }
                  onChange={(e) =>
                    onUpdate(row.fileId, "billing_account", e.target.value)
                  }
                  disabled={busy || !row.provider}
                >
                  <option value="">
                    {row.provider ? "Select account" : "Select provider first"}
                  </option>
                  {accounts.map((account) => (
                    <option
                      key={`${account.provider}-${account.billing_account}`}
                      value={normalizeBillingAccount(account.billing_account)}
                    >
                      {account.show_as || account.billing_account}
                    </option>
                  ))}
                </CustomSelect>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone-500">
                  GL Code<span className="text-red-400">*</span>
                </label>
                <CustomSelect
                  className={fieldClass}
                  value={
                    glCodesForDropdown(glCodes, row.gl_code).some(
                      (code) => String(code.gl_code) === String(row.gl_code || ""),
                    )
                      ? String(row.gl_code || "")
                      : ""
                  }
                  onChange={(e) => onUpdate(row.fileId, "gl_code", e.target.value)}
                  disabled={busy}
                >
                  <option value="">Select GL code</option>
                  {glCodesForDropdown(glCodes, row.gl_code).map((code) => (
                    <option key={code.gl_code} value={code.gl_code}>
                      {formatGlCodeLabel(code.gl_code, code.description)}
                    </option>
                  ))}
                </CustomSelect>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone-500">
                  MRC<span className="text-red-400">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  className={numberFieldClass}
                  value={row.monthly_mrc}
                  onChange={(e) =>
                    onUpdate(row.fileId, "monthly_mrc", e.target.value)
                  }
                  onWheel={(e) => e.target.blur()}
                  disabled={busy}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone-500">
                  Taxes and Surcharges
                </label>
                <input
                  type="number"
                  step="0.01"
                  className={numberFieldClass}
                  value={row.taxes_and_surcharges}
                  onChange={(e) =>
                    onUpdate(row.fileId, "taxes_and_surcharges", e.target.value)
                  }
                  onWheel={(e) => e.target.blur()}
                  disabled={busy}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone-500">
                  Total Invoice Amount<span className="text-red-400">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  className={totalFieldClass}
                  value={row.total_invoice_amount}
                  onChange={(e) =>
                    onUpdate(row.fileId, "total_invoice_amount", e.target.value)
                  }
                  onWheel={(e) => e.target.blur()}
                  disabled={busy}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-stone-500">
                  Due Date
                </label>
                <YmdDateInput
                  className={fieldClass}
                  value={row.due_date}
                  onChange={(e) => onUpdate(row.fileId, "due_date", e.target.value)}
                  disabled={busy}
                />
              </div>
              {amountCheck ? (
                <p className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2.5 text-sm text-stone-600">
                  MRC plus taxes is{" "}
                  <span className="font-semibold text-stone-900">
                    {formatPreviewAmount(amountCheck.sum)}
                  </span>{" "}
                  against a total of{" "}
                  <span className="font-semibold text-stone-900">
                    {formatPreviewAmount(amountCheck.total)}
                  </span>
                  . Difference of{" "}
                  <span className="font-semibold text-stone-900">
                    {formatPreviewAmount(amountCheck.diff)}
                  </span>
                  , likely one time charges or credits on the invoice.
                </p>
              ) : (
                <p className="text-xs text-stone-500">
                  Fill MRC and the total and this line will check the arithmetic for you.
                </p>
              )}
              {row.notConfiguredMessage ? (
                <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {row.notConfiguredMessage}
                </p>
              ) : null}
            </div>
          </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-3 border-t border-stone-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-stone-500">
            Esc close{showFields ? " · Ctrl + Enter save" : ""}
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              {showFields ? "Cancel" : "Close"}
            </button>
            {showFields && hasNextFlagged && typeof onSaveAndNext === "function" ? (
              <button
                type="button"
                onClick={onSaveAndNext}
                disabled={busy}
                className="rounded-md border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-800 hover:bg-stone-50 disabled:opacity-50"
              >
                Save and next flagged
              </button>
            ) : null}
            {showFields && typeof onSaveAndClose === "function" ? (
              <button
                type="button"
                onClick={onSaveAndClose}
                disabled={busy}
                className="rounded-md border border-transparent bg-stone-900 px-4 py-2 text-sm font-medium text-white hover:bg-stone-800 disabled:opacity-50"
              >
                Save and close
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceExtractionPdfPreviewModal;
